import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const messages = {
  en: [
    { text: '5 reservations made in the last 48 hours!', icon: 'home' },
    { text: 'Availability is decreasing rapidly. Book now!', icon: 'trending' },
    { text: 'Just booked!', icon: 'clock' },
    { text: 'Official website best rate guaranteed', icon: 'thumbs' },
  ],
  pt: [
    { text: '5 reservas feitas nas últimas 48 horas!', icon: 'home' },
    { text: 'A disponibilidade está a diminuir rapidamente. Reserve já!', icon: 'trending' },
    { text: 'Acabou de ser reservado!', icon: 'clock' },
    { text: 'Melhor tarifa garantida no site oficial', icon: 'thumbs' },
  ],
  es: [
    { text: '5 reservas realizadas en las últimas 48 horas!', icon: 'home' },
    { text: 'La disponibilidad está disminuyendo rápidamente. ¡Reserve ahora!', icon: 'trending' },
    { text: '¡Recién reservado!', icon: 'clock' },
    { text: 'Mejor tarifa garantizada en el sitio oficial', icon: 'thumbs' },
  ],
  de: [
    { text: '5 Reservierungen in den letzten 48 Stunden!', icon: 'home' },
    { text: 'Die Verfügbarkeit nimmt schnell ab. Jetzt buchen!', icon: 'trending' },
    { text: 'Gerade gebucht!', icon: 'clock' },
    { text: 'Bestpreisgarantie auf der offiziellen Website', icon: 'thumbs' },
  ],
  fr: [
    { text: '5 réservations effectuées ces dernières 48 heures !', icon: 'home' },
    { text: 'La disponibilité diminue rapidement. Réservez maintenant !', icon: 'trending' },
    { text: "Vient d'être réservé !", icon: 'clock' },
    { text: 'Meilleur tarif garanti sur le site officiel', icon: 'thumbs' },
  ],
};

const icons = {
  home: <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-white stroke-2"><path d="M3 12l9-8 9 8M5 12v7a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  trending: <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-white stroke-2"><path d="M23 6l-9.5 9.5-5-5L1 18" strokeLinecap="round" strokeLinejoin="round"/><path d="M17 6h6v6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  clock: <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-white stroke-2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  thumbs: <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-white stroke-2"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
};

// Pages where the banner should NOT show
const EXCLUDED_PATHS = ['/qapanel', '/checkout', '/confirmation', '/privacy-policy', '/terms-of-service', '/_stories'];

export const GlobalUrgencyBanner = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = i18n.language?.split('-')[0]?.split('@')[0] || 'en';
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const msgs = messages[lang] || messages.en;

  useEffect(() => {
    if (dismissed) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(prev => (prev + 1) % msgs.length);
        setVisible(true);
      }, 400);
    }, 15000);
    return () => clearInterval(interval);
  }, [msgs.length, dismissed]);

  // Hide on excluded pages
  const path = location.pathname;
  const isExcluded = EXCLUDED_PATHS.some(p => path.includes(p));
  if (isExcluded || dismissed) return null;

  const msg = msgs[index];

  return (
    <div className="hidden md:block fixed bottom-6 right-6 z-50" style={{ maxWidth: '280px' }} data-testid="global-urgency-banner">
      <div
        className={`bg-[#8bc28b] rounded-lg pl-3 pr-2 py-2.5 flex items-start gap-2.5 shadow-lg transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
      >
        <div className="flex-shrink-0 mt-0.5">{icons[msg.icon]}</div>
        <p className="text-white font-semibold text-xs leading-snug flex-1">{msg.text}</p>
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 w-5 h-5 rounded-full bg-black/30 text-white flex items-center justify-center text-[10px] hover:bg-black/50 transition-colors"
          data-testid="global-urgency-dismiss"
        >
          &#x2715;
        </button>
      </div>
    </div>
  );
};
